import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "carousel-container" }
const _hoisted_2 = { class: "carousel-overlay" }
const _hoisted_3 = { class: "carousel-content" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "carousel-indicators" }
const _hoisted_8 = ["onClick", "aria-label"]

import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

// 从接口获取的轮播图数据类型定义
interface ApiCarouselItem {
  id: number;
  imgUrl: string;
  imgUrlen: string;
  sort: number;
}

// 内部使用的轮播图数据类型定义
interface CarouselItem {
  imageUrl: string;
  title?: string;
  description?: string;
  buttonText?: string;
  link?: string;
  id?: number;
}

// Props 定义
interface Props {
  // 外部传入的轮播数据
  data?: CarouselItem[];
  // 自动播放间隔时间（毫秒）
  interval?: number;
  // 过渡动画速度（毫秒）
  transition?: number;
  // 是否自动播放
  autoplay?: boolean;
}

// 默认值

export default /*@__PURE__*/_defineComponent({
  __name: 'CarouselComponent',
  props: {
    data: { default: () => [] },
    interval: { default: 5000 },
    transition: { default: 500 },
    autoplay: { type: Boolean, default: true }
  },
  emits: ["item-click", "slide-change"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// Emits 定义
const emit = __emit;

// 状态管理
const currentSlide = ref(0);
const transitionSpeed = ref(props.transition);
let autoplayTimer: number | null = null;

// 静态轮播数据（预留接口位置后使用）
const defaultCarouselData: CarouselItem[] = [
  {
    imageUrl: 'https://evlife.com.hk/cdn/shop/files/2800x1000_wallbox_pulsar_1.png?v=1751281296&width=2600',
    title: '智能充电解决方案',
    description: '为您的电动车提供高效、安全的充电体验',
    buttonText: '了解更多'
  },
  {
    imageUrl: 'https://picsum.photos/id/1071/1600/900',
    title: 'EV 屋苑充电易',
    description: '专为住宅社区设计的充电解决方案',
    buttonText: '查看详情'
  },
  {
    imageUrl: 'https://evlife.com.hk/cdn/shop/files/ntt_charging_cable_01.png?v=1733816048&width=2600',
    title: '商用充电方案',
    description: '为商业场所提供定制化充电设施',
    buttonText: '联系我们'
  },
  {
    imageUrl: 'https://picsum.photos/id/1076/1600/900',
    title: '商用充电方案',
    description: '为商业场所提供定制化充电设施',
    buttonText: '联系我们'
  }
];

// 获取当前语言
const { locale } = useI18n();
const currentLanguage = ref(locale.value);

// 监听语言变化
watch(() => locale.value, (newLang) => {
  currentLanguage.value = newLang;
  // 语言变化时更新轮播图数据
  updateCarouselImages();
});

// 存储从接口获取的原始轮播图数据
const apiCarouselData = ref<ApiCarouselItem[]>([]);

// 使用外部数据或默认数据
const carouselData = ref<CarouselItem[]>(props.data.length > 0 ? props.data : defaultCarouselData);

// 根据当前语言更新轮播图图片
const updateCarouselImages = () => {
  if (apiCarouselData.value.length > 0) {
    carouselData.value = apiCarouselData.value.map(item => ({
      id: item.id,
      imageUrl: currentLanguage.value === 'en' ? item.imgUrlen.replace(/^\s*`|`\s*$/g, '') : item.imgUrl.replace(/^\s*`|`\s*$/g, ''),
      // 可以根据需要添加默认的标题和描述
      title: '',
      description: '',
      buttonText: '了解更多'
    }));
  }
};

// 从接口获取轮播图数据
const fetchCarouselData = async () => {
  try {
    const response = await fetch('/jeecg-boot/energycube/carousel/queryAllCarousel');
    const data = await response.json();
    if (data.code === 200 && data.result) {
      // 保存原始数据
      apiCarouselData.value = data.result;
      // 更新轮播图图片
      updateCarouselImages();
    }
    console.log("轮播图数据：", apiCarouselData.value);
  } catch (error) {
    console.error('获取轮播图数据失败:', error);
    // 接口调用失败时，使用默认数据
    if (carouselData.value.length === 0) {
      carouselData.value = defaultCarouselData;
    }
  }
};

// 监听数据变化
watch(() => props.data, (newData) => {
  if (newData && newData.length > 0) {
    carouselData.value = newData;
    currentSlide.value = 0; // 重置到第一张
  }
}, { deep: true });

// 监听间隔时间变化
watch(() => props.interval, (newInterval) => {
  if (props.autoplay) {
    stopAutoplay();
    startAutoplay(newInterval);
  }
});

// 监听自动播放状态变化
watch(() => props.autoplay, (newAutoplay) => {
  if (newAutoplay) {
    startAutoplay();
  } else {
    stopAutoplay();
  }
});

// 下一张
const nextSlide = () => {
  transitionSpeed.value = props.transition;
  currentSlide.value = (currentSlide.value + 1) % carouselData.value.length;
  emit('slide-change', currentSlide.value);
  // 手动切换后重新启动自动播放
  restartAutoplay();
};

// 上一张
const prevSlide = () => {
  transitionSpeed.value = props.transition;
  currentSlide.value = (currentSlide.value - 1 + carouselData.value.length) % carouselData.value.length;
  emit('slide-change', currentSlide.value);
  // 手动切换后重新启动自动播放
  restartAutoplay();
};

// 跳转到指定幻灯片
const goToSlide = (index: number) => {
  if (index !== currentSlide.value) {
    transitionSpeed.value = props.transition;
    currentSlide.value = index;
    emit('slide-change', currentSlide.value);
    // 手动切换后重新启动自动播放
    restartAutoplay();
  }
};

// 重新启动自动播放
const restartAutoplay = () => {
  stopAutoplay();
  if (props.autoplay && carouselData.value.length > 1) {
    setTimeout(() => {
      startAutoplay();
    }, props.interval);
  }
};

// 处理点击事件
const handleItemClick = (item: CarouselItem) => {
  emit('item-click', item);
};

// 开始自动播放
const startAutoplay = (interval?: number) => {
  stopAutoplay();
  autoplayTimer = window.setInterval(() => {
    nextSlide();
  }, interval || props.interval);
};

// 停止自动播放
const stopAutoplay = () => {
  if (autoplayTimer) {
    clearInterval(autoplayTimer);
    autoplayTimer = null;
  }
};

// 生命周期钩子
onMounted(() => {
  // 组件挂载时获取轮播图数据
  fetchCarouselData();
  if (props.autoplay && carouselData.value.length > 1) {
    startAutoplay();
  }
});

onUnmounted(() => {
  stopAutoplay();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "carousel-slide",
      style: _normalizeStyle({ transform: `translateX(-${currentSlide.value * 100}%)`, transition: `transform ${transitionSpeed.value}ms ease-in-out` })
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(carouselData.value, (item) => {
        return (_openBlock(), _createElementBlock("div", {
          key: item.id || item.imageUrl,
          class: "carousel-item",
          style: _normalizeStyle({ backgroundImage: `url(${item.imageUrl})` })
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              (item.title && item.title.trim())
                ? (_openBlock(), _createElementBlock("h2", _hoisted_4, _toDisplayString(item.title), 1))
                : _createCommentVNode("", true),
              (item.description && item.description.trim())
                ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(item.description), 1))
                : _createCommentVNode("", true),
              (item.buttonText)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 2,
                    class: "carousel-button",
                    onClick: ($event: any) => (handleItemClick(item))
                  }, _toDisplayString(item.buttonText), 9, _hoisted_6))
                : _createCommentVNode("", true)
            ])
          ])
        ], 4))
      }), 128))
    ], 4),
    _createElementVNode("button", {
      class: "carousel-control prev",
      onClick: prevSlide,
      "aria-label": "上一张"
    }, [...(_cache[0] || (_cache[0] = [
      _createElementVNode("svg", {
        width: "24",
        height: "24",
        viewBox: "0 0 24 24",
        fill: "currentColor"
      }, [
        _createElementVNode("path", { d: "M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" })
      ], -1)
    ]))]),
    _createElementVNode("button", {
      class: "carousel-control next",
      onClick: nextSlide,
      "aria-label": "下一张"
    }, [...(_cache[1] || (_cache[1] = [
      _createElementVNode("svg", {
        width: "24",
        height: "24",
        viewBox: "0 0 24 24",
        fill: "currentColor"
      }, [
        _createElementVNode("path", { d: "M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" })
      ], -1)
    ]))]),
    _createElementVNode("div", _hoisted_7, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(carouselData.value, (item, index) => {
        return (_openBlock(), _createElementBlock("button", {
          key: item.id || index,
          class: _normalizeClass(["indicator", { active: currentSlide.value === index }]),
          onClick: ($event: any) => (goToSlide(index)),
          "aria-label": `转到幻灯片 ${index + 1}`
        }, null, 10, _hoisted_8))
      }), 128))
    ])
  ]))
}
}

})